import { DIAGNOSTIC_DEFINITION_KIND, PROTOCOL_DEFINITION_KIND, TREATMENT_DEFINITION_KIND } from "@repo/api";
import { ProtocolDefinition } from "@repo/domain";
import type { Collection, InitialQueryBuilder, UtilsRecord } from "@tanstack/db";
import { and, coalesce, concat, eq, ilike, max, or } from "@tanstack/db";
import { readProtocolDefinition } from "../fhir/protocol-definition-storage";
import type { ActivityDefinitionRow, PlanDefinitionRow } from "../fhir/types";

type Definitions = Collection<PlanDefinitionRow, string, UtilsRecord>;
type ProcedureDefinitions = Collection<ActivityDefinitionRow, string, UtilsRecord>;

export interface ResolvedProcedureRow {
  catalogModelKind?: string;
  catalogVersion: number;
  status?: string;
  url?: string;
}

export function protocolBookingsQueryKey(
  definitionCollectionId: string,
  procedureCollectionId: string,
  procedureDefinitions: readonly ResolvedProcedureRow[],
  search?: string,
) {
  return [
    definitionCollectionId,
    procedureCollectionId,
    "protocol-bookings",
    "resolved-procedure-v2",
    procedureDefinitions,
    search ?? null,
  ] as const;
}

export function protocolBookingQueryKey(
  definitionCollectionId: string,
  procedureCollectionId: string,
  procedureDefinitions: readonly ResolvedProcedureRow[],
  pin: { protocolUrl: string; protocolVersion: string } | undefined,
) {
  return [
    definitionCollectionId,
    procedureCollectionId,
    "protocol-booking",
    "resolved-procedure-v2",
    procedureDefinitions,
    pin?.protocolUrl ?? null,
    pin?.protocolVersion ?? null,
  ] as const;
}

/** Neutral catalog rows consumed by the functional nested-protocol decoder. */
export function buildProtocolProcedureCatalogQuery(query: InitialQueryBuilder, procedures: ProcedureDefinitions) {
  const latestVersions = query
    .from({ candidate: procedures })
    .where(({ candidate }) =>
      or(
        eq(candidate.catalogModelKind, DIAGNOSTIC_DEFINITION_KIND),
        eq(candidate.catalogModelKind, TREATMENT_DEFINITION_KIND),
      ),
    )
    .groupBy(({ candidate }) => candidate.url)
    .groupBy(({ candidate }) => candidate.catalogModelKind)
    .select(({ candidate }) => ({
      catalogModelKind: candidate.catalogModelKind,
      catalogVersion: max(candidate.catalogVersion),
      url: candidate.url,
    }));
  return query
    .from({ resolvedProcedure: procedures })
    .innerJoin({ latest: latestVersions }, ({ latest, resolvedProcedure }) => eq(resolvedProcedure.url, latest.url))
    .where(({ latest, resolvedProcedure }) =>
      and(
        eq(resolvedProcedure.catalogModelKind, latest.catalogModelKind),
        eq(resolvedProcedure.catalogVersion, latest.catalogVersion),
      ),
    )
    .select(({ resolvedProcedure }) => ({
      catalogModelKind: resolvedProcedure.catalogModelKind,
      catalogVersion: resolvedProcedure.catalogVersion,
      status: resolvedProcedure.status,
      url: resolvedProcedure.url,
    }));
}

function decodeBookingDefinition(definition: PlanDefinitionRow, procedureDefinitions: readonly ResolvedProcedureRow[]) {
  try {
    const protocol = readProtocolDefinition(definition);
    const steps = protocol.steps.map((step) => ({
      orders: step.occurrences.map((occurrence) => {
        if (!occurrence.id) throw new Error("Protocol occurrence has no stable identity");
        const matches = procedureDefinitions.filter((candidate) => candidate.url === occurrence.definition);
        const currentOfKind = (kind: string) => {
          const current = matches.filter((candidate) => candidate.catalogModelKind === kind);
          if (current.length > 1) throw new Error("Procedure lineage has an ambiguous current revision");
          return current[0];
        };
        const diagnostic = currentOfKind(DIAGNOSTIC_DEFINITION_KIND);
        const treatment = currentOfKind(TREATMENT_DEFINITION_KIND);
        if (diagnostic && treatment) throw new Error("Procedure lineage resolves to two catalog kinds");
        const resolved = diagnostic ?? treatment;
        if (
          resolved?.status !== "active" ||
          !resolved.url ||
          !Number.isInteger(resolved.catalogVersion) ||
          resolved.catalogVersion < 1
        ) {
          throw new Error("Protocol occurrence does not resolve to an active identified revision");
        }
        return {
          definitionCanonical: `${resolved.url}|${resolved.catalogVersion}`,
          orderId: occurrence.id as string,
          sourceOccurrenceId: occurrence.id as string,
        };
      }),
    }));
    if (steps.some((step) => step.orders.length === 0)) return undefined;
    const durationMinutes = new ProtocolDefinition(protocol).durationMinutes;
    if (!Number.isFinite(durationMinutes) || durationMinutes <= 0) return undefined;
    return { durationMinutes, steps, examUrls: protocol.exams.map(String) };
  } catch {
    return undefined;
  }
}

export function buildProtocolBookingsQuery(
  query: InitialQueryBuilder,
  definitions: Definitions,
  procedureDefinitions: readonly ResolvedProcedureRow[],
  search?: string,
) {
  const latestVersions = query
    .from({ candidate: definitions })
    .where(({ candidate }) => eq(candidate.catalogModelKind, PROTOCOL_DEFINITION_KIND))
    .groupBy(({ candidate }) => candidate.url)
    .select(({ candidate }) => ({ url: candidate.url, version: max(candidate.catalogVersion) }));
  const current = query
    .from({ definition: definitions })
    .innerJoin({ latest: latestVersions }, ({ definition, latest }) => eq(definition.url, latest.url))
    .where(({ definition, latest }) =>
      and(
        eq(definition.catalogModelKind, PROTOCOL_DEFINITION_KIND),
        eq(definition.catalogVersion, latest.version),
        eq(definition.status, "active"),
      ),
    );
  const matching = search
    ? current.where(({ definition }) =>
        or(
          ilike(definition.title, `%${search}%`),
          ilike(definition.name, `%${search}%`),
          ilike(definition.description, `%${search}%`),
        ),
      )
    : current;
  const projected = matching
    .orderBy(({ definition }) => coalesce(definition.title, definition.name, "Untitled Protocol"), "asc")
    .select(({ definition }) => ({
      canonical: concat(definition.url, "|", definition.catalogVersion),
      protocolUrl: coalesce(definition.url, ""),
      protocolVersion: concat(definition.catalogVersion),
      title: coalesce(definition.title, definition.name, "Untitled Protocol"),
      description: definition.description,
      definition,
    }));
  return query
    .from({ booking: projected })
    .fn.where(({ booking }) => decodeBookingDefinition(booking.definition, procedureDefinitions) !== undefined)
    .fn.select(({ booking }) => {
      const decoded = decodeBookingDefinition(booking.definition, procedureDefinitions);
      if (!decoded)
        throw new Error(`PlanDefinition/${booking.definition.id ?? "<unknown>"} is not eligible for booking`);
      const { definition: _definition, ...dto } = booking;
      return { ...dto, ...decoded };
    })
    .distinct();
}

export function buildProtocolBookingQuery(
  query: InitialQueryBuilder,
  definitions: Definitions,
  procedureDefinitions: readonly ResolvedProcedureRow[],
  pin: { protocolUrl: string; protocolVersion: string },
) {
  const latestVersion = query
    .from({ candidate: definitions })
    .where(({ candidate }) =>
      and(eq(candidate.catalogModelKind, PROTOCOL_DEFINITION_KIND), eq(candidate.url, pin.protocolUrl)),
    )
    .select(({ candidate }) => ({ version: max(candidate.catalogVersion) }));
  const projected = query
    .from({ definition: definitions })
    .innerJoin({ latest: latestVersion }, ({ definition, latest }) => eq(definition.catalogVersion, latest.version))
    .where(({ definition, latest }) =>
      and(
        eq(definition.catalogModelKind, PROTOCOL_DEFINITION_KIND),
        eq(definition.url, pin.protocolUrl),
        eq(definition.catalogVersion, Number(pin.protocolVersion)),
        eq(definition.catalogVersion, latest.version),
        eq(definition.status, "active"),
      ),
    )
    .select(({ definition }) => ({
      canonical: concat(definition.url, "|", definition.catalogVersion),
      protocolUrl: coalesce(definition.url, ""),
      protocolVersion: concat(definition.catalogVersion),
      title: coalesce(definition.title, definition.name, "Untitled Protocol"),
      description: definition.description,
      definition,
    }));
  return query
    .from({ booking: projected })
    .fn.where(({ booking }) => decodeBookingDefinition(booking.definition, procedureDefinitions) !== undefined)
    .fn.select(({ booking }) => {
      const decoded = decodeBookingDefinition(booking.definition, procedureDefinitions);
      if (!decoded)
        throw new Error(`PlanDefinition/${booking.definition.id ?? "<unknown>"} is not eligible for booking`);
      const { definition: _definition, ...dto } = booking;
      return { ...dto, ...decoded };
    })
    .findOne();
}
